import React from "react";

import httpr from "lib/httpRequest";
import { BASE_URL, isLocal } from "lib/config";

import ReactLoading from "react-loading";
import Tooltip from "@material-ui/core/Tooltip";

import AlertTable from "components/Table/AlertTable.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";

class MachineErrors extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      isLoading: true,
      expiredLocations: [],
      expiringLocations: [],
      hasFetched: false,
    };
  }

  // Helper functions to check license status (matching DashboardTabs pattern)
  getLocationLicenseStatus = (locationName, locationLicenses) => {
    if (!locationLicenses || locationLicenses.length === 0) {
      return null;
    }
    // Find license data by matching locationName
    // API response uses location name as key (may have underscores)
    // License data has locationName field (clean name from DB)
    const licenseData = locationLicenses.find(
      loc => {
        if (!loc.locationName) return false;
        
        // Normalize both names for comparison
        const normalizedApiName = locationName.replace(/_/g, " ").trim().toLowerCase();
        const normalizedLicenseName = loc.locationName.trim().toLowerCase();
        
        // Try exact match first
        if (normalizedApiName === normalizedLicenseName) return true;
        
        // Try matching with normalized spaces
        const normalizedApiNameSpaces = normalizedApiName.replace(/\s+/g, " ");
        const normalizedLicenseNameSpaces = normalizedLicenseName.replace(/\s+/g, " ");
        return normalizedApiNameSpaces === normalizedLicenseNameSpaces;
      }
    );
    return licenseData ? licenseData.license : null;
  };

  isLocationExpired = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license) return false; // Treat null/missing as valid
    return license.isValid === 0;
  };

  isLocationExpiringSoon = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license || license.isValid === 0) return false;
    const daysUntilExpiration = license.daysUntilExpiration;
    return daysUntilExpiration !== null && daysUntilExpiration <= 30 && daysUntilExpiration > 0;
  };

  getDaysUntilExpiration = (locationName, locationLicenses) => {
    const license = this.getLocationLicenseStatus(locationName, locationLicenses);
    if (!license) return null;
    return license.daysUntilExpiration;
  };

  componentDidMount() {
    this._isMounted = true;
    // If we have license data and settings, fetch errors
    if (this.props.locationLicenses && 
        this.props.locationLicenses.length > 0 && 
        this.props.memberSettings.machineErrorRange.length > 0) {
      this.setState({ isLoading: true });
      this.getMachineErrors(this.props.locationLicenses);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if we need to fetch errors
    const settingsChanged = prevProps.memberSettings.machineErrorRange !==
      this.props.memberSettings.machineErrorRange;
    
    const licenseDataChanged = prevProps.locationLicenses !== this.props.locationLicenses;
    const licenseDataAvailable = this.props.locationLicenses && 
      this.props.locationLicenses.length > 0 && 
      (!prevProps.locationLicenses || prevProps.locationLicenses.length === 0);
    
    if (settingsChanged && this.props.locationLicenses && this.props.locationLicenses.length > 0 && this._isMounted === true) {
      // Settings changed, refetch with current license data
      this.setState({ 
        isLoading: true, 
        errors: [],
        expiredLocations: [],
        expiringLocations: []
      });
      this.getMachineErrors(this.props.locationLicenses);
    } else if (licenseDataAvailable && this.props.memberSettings.machineErrorRange.length > 0 && !this.state.hasFetched && this._isMounted === true) {
      // License data just became available, fetch errors
      this.setState({ isLoading: true });
      this.getMachineErrors(this.props.locationLicenses);
    } else if (licenseDataChanged && this.props.locationLicenses && this.props.locationLicenses.length > 0 && this.props.memberSettings.machineErrorRange.length > 0) {
      // License data changed, refetch
      this.setState({ 
        isLoading: true,
        hasFetched: false,
        errors: [],
        expiredLocations: [],
        expiringLocations: []
      });
      this.getMachineErrors(this.props.locationLicenses);
    }
  }

  getMachineErrors = (locationLicenses = []) => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    // Fetch data from api
    fetch
      .post(`${BASE_URL}/api/alerts/error`, {
        timeInterval: this.props.memberSettings.machineErrorRange,
      })
      .then((response) => {
        if (this._isMounted === true) {
          const errors = response.data || [];
          
          // Separate expired and expiring locations
          const expiredLocations = [];
          const expiringLocations = [];
          const validErrors = [];
          
          errors.forEach((errorObj) => {
            Object.keys(errorObj).forEach((locationName) => {
              const isExpired = this.isLocationExpired(locationName, locationLicenses);
              const isExpiringSoon = this.isLocationExpiringSoon(locationName, locationLicenses);
              
              if (isExpired) {
                // Don't include error data for expired locations - show License Expired card instead
                expiredLocations.push({
                  locationName: locationName,
                });
              } else {
                // Valid or expiring location - include in normal display
                validErrors.push(errorObj);
                
                if (isExpiringSoon) {
                  // Track expiring locations for warning display
                  expiringLocations.push({
                    locationName: locationName,
                    daysUntilExpiration: this.getDaysUntilExpiration(locationName, locationLicenses)
                  });
                }
              }
            });
          });
          
          this.setState({
            errors: validErrors,
            expiredLocations: expiredLocations,
            expiringLocations: expiringLocations,
            isLoading: false,
            hasFetched: true,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        if (this._isMounted === true) {
          this.setState({ isLoading: false });
        }
      });
  };

  render() {
    const { classes, locationLicenses = [] } = this.props;

    // Render expired location cards (License Expired message)
    const expiredLocationCards = this.state.expiredLocations.map((expiredLoc) => {
      const locationName = expiredLoc.locationName;
      return (
        <GridItem key={`${locationName}ExpiredGrid`} xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="danger">
              <h4 className={classes.cardTitleWhite}>
                🔒 {locationName.replace(/_/g, " ")} - License Expired
              </h4>
            </CardHeader>
            <CardBody>
              <div style={{ 
                padding: '20px', 
                textAlign: 'center',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                margin: '20px'
              }}>
                <h3 style={{ color: '#d32f2f', margin: '0 0 10px 0' }}>
                  🔒 License Expired
                </h3>
                <p style={{ color: '#666', margin: '0' }}>
                  This location's license has expired. {isLocal === "true" || isLocal === true 
                    ? "Please click Renew License and follow the instructions to send a renewal token to our support team."
                    : "Please contact our support team for renewal."}
                </p>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      );
    });

    // Render error cards for valid/expiring locations
    const errorCards = this.state.errors.map((errorsByLocation) => {
      return Object.keys(errorsByLocation).map((locationName) => {
        const locData = errorsByLocation[locationName];
        const isExpiringSoon = this.isLocationExpiringSoon(locationName, locationLicenses);
        const daysUntilExpiration = this.getDaysUntilExpiration(locationName, locationLicenses);
        
        // Build title with warning if expiring soon
        let titleText = locationName.replace(/_/g, " ");
        if (isExpiringSoon) {
          titleText = `⚠️ ${titleText} - Renewal due in ${daysUntilExpiration} days`;
        }

        return (
          <GridItem key={`${locationName}ErrorsGrid`} xs={12} sm={12} md={12}>
            <Card>
              {isExpiringSoon ? (
                <Tooltip
                  title={`License renewal due soon. ${isLocal === "true" || isLocal === true 
                    ? "Please click Renew License and follow the instructions to send a renewal token to our support team."
                    : "Please contact our support team for renewal."}`}
                  placement="top"
                  arrow
                >
                  <div>
                    <CardHeader color="warning">
                      <h4 className={classes.cardTitleWhite}>
                        {titleText}
                      </h4>
                    </CardHeader>
                  </div>
                </Tooltip>
              ) : (
                <CardHeader color="success">
                  <h4 className={classes.cardTitleWhite}>
                    {titleText}
                  </h4>
                </CardHeader>
              )}
              <CardBody>
                <AlertTable
                  key={`${locationName}Errors`}
                  tableHeaderColor="primary"
                  tableData={locData}
                  tableTitle=""
                  tableType="Errors"
                  dismissAlert={this.props.dismissAlert}
                  locationName={locationName}
                />
              </CardBody>
            </Card>
          </GridItem>
        );
      });
    });

    // Combine all cards: expired first, then valid/expiring
    const allCards = [...expiredLocationCards, ...errorCards];

    return (
      <div>
        {this.state.isLoading === true ? (
          <div>
            <CardBody>
              <ReactLoading
                type="spin"
                color="#000000"
                style={{
                  width: "20%",
                  margin: "auto",
                }}
              />
            </CardBody>
          </div>
        ) : (
          <GridContainer key="Error">{allCards}</GridContainer>
        )}
      </div>
    );
  }
}

export default MachineErrors;



// WEBPACK FOOTER //
// ./src/components/Alerts/MachineErrors.jsx